// src/components/Admin/GeographyLibrary.jsx
import React, { useState, useEffect } from "react";
import {
  Globe,
  Upload,
  Trash2,
  RefreshCw,
  Check,
  AlertCircle,
  Map,
  Layers,
} from "lucide-react";
import {
  listGeographies,
  registerGeography,
  deleteGeography,
  uploadFile,
  deleteFile,
} from "../../services/adminService";
import FileUploader from "./shared/FileUploader";
import ConfirmDialog from "./shared/ConfirmDialog";
import { useToast } from "./shared/ToastContext";

// US state list for dropdown
const US_STATES = [
  { abbr: "AL", name: "Alabama" },
  { abbr: "AK", name: "Alaska" },
  { abbr: "AZ", name: "Arizona" },
  { abbr: "AR", name: "Arkansas" },
  { abbr: "CA", name: "California" },
  { abbr: "CO", name: "Colorado" },
  { abbr: "CT", name: "Connecticut" },
  { abbr: "DE", name: "Delaware" },
  { abbr: "FL", name: "Florida" },
  { abbr: "GA", name: "Georgia" },
  { abbr: "HI", name: "Hawaii" },
  { abbr: "ID", name: "Idaho" },
  { abbr: "IL", name: "Illinois" },
  { abbr: "IN", name: "Indiana" },
  { abbr: "IA", name: "Iowa" },
  { abbr: "KS", name: "Kansas" },
  { abbr: "KY", name: "Kentucky" },
  { abbr: "LA", name: "Louisiana" },
  { abbr: "ME", name: "Maine" },
  { abbr: "MD", name: "Maryland" },
  { abbr: "MA", name: "Massachusetts" },
  { abbr: "MI", name: "Michigan" },
  { abbr: "MN", name: "Minnesota" },
  { abbr: "MS", name: "Mississippi" },
  { abbr: "MO", name: "Missouri" },
  { abbr: "MT", name: "Montana" },
  { abbr: "NE", name: "Nebraska" },
  { abbr: "NV", name: "Nevada" },
  { abbr: "NH", name: "New Hampshire" },
  { abbr: "NJ", name: "New Jersey" },
  { abbr: "NM", name: "New Mexico" },
  { abbr: "NY", name: "New York" },
  { abbr: "NC", name: "North Carolina" },
  { abbr: "ND", name: "North Dakota" },
  { abbr: "OH", name: "Ohio" },
  { abbr: "OK", name: "Oklahoma" },
  { abbr: "OR", name: "Oregon" },
  { abbr: "PA", name: "Pennsylvania" },
  { abbr: "RI", name: "Rhode Island" },
  { abbr: "SC", name: "South Carolina" },
  { abbr: "SD", name: "South Dakota" },
  { abbr: "TN", name: "Tennessee" },
  { abbr: "TX", name: "Texas" },
  { abbr: "UT", name: "Utah" },
  { abbr: "VT", name: "Vermont" },
  { abbr: "VA", name: "Virginia" },
  { abbr: "WA", name: "Washington" },
  { abbr: "WV", name: "West Virginia" },
  { abbr: "WI", name: "Wisconsin" },
  { abbr: "WY", name: "Wyoming" },
  { abbr: "DC", name: "District of Columbia" },
];

const GeographyLibrary = () => {
  const toast = useToast();
  const [geographies, setGeographies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Upload form state
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadState, setUploadState] = useState("");
  const [uploadGeoType, setUploadGeoType] = useState("");
  const [uploadRegionLabel, setUploadRegionLabel] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchGeographies = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listGeographies();
      setGeographies(data);
    } catch (err) {
      setError(err.message || "Failed to load geographies");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGeographies();
  }, []);

  // Generate a URL-safe slug from a region label
  const toRegionSlug = (label) => {
    if (!label?.trim()) return "";
    return label
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "")
      .slice(0, 40);
  };

  // Computed region slug from the label
  const uploadRegionSlug = toRegionSlug(uploadRegionLabel);

  // Check if a state+geoType+region combo already exists
  const geoExists = (state, geoType, regionSlug = "") => {
    return geographies.some(
      (g) =>
        g.state === state &&
        g.geographyType === geoType &&
        (g.region || "") === regionSlug,
    );
  };

  // Get state name from abbreviation
  const getStateName = (abbr) => {
    return US_STATES.find((s) => s.abbr === abbr)?.name || abbr;
  };

  // Handle upload
  const handleUpload = async (file) => {
    if (!uploadState || !uploadGeoType) {
      setUploadError("Select a state and geography type first");
      return;
    }

    if (geoExists(uploadState, uploadGeoType, uploadRegionSlug)) {
      const label = uploadRegionSlug
        ? `${uploadState} ${uploadGeoType} (${uploadRegionLabel})`
        : `${uploadState} ${uploadGeoType}`;
      setUploadError(`${label} already exists. Delete it first to re-upload.`);
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      // Build storage path: shared/geojson/{geoType}/{state}.json or {state}_{region}.json
      const fileName = uploadRegionSlug
        ? `${uploadState}_${uploadRegionSlug}.json`
        : `${uploadState}.json`;
      const storagePath = `shared/geojson/${uploadGeoType}/${fileName}`;
      await uploadFile(storagePath, file);

      // Register in Firestore geography index
      await registerGeography(uploadState, uploadGeoType, {
        stateName: getStateName(uploadState),
        region: uploadRegionSlug || null,
        regionLabel: uploadRegionLabel.trim() || null,
      });

      // Refresh list
      await fetchGeographies();

      // Reset form
      setShowUploadForm(false);
      setUploadState("");
      setUploadGeoType("");
      setUploadRegionLabel("");

      toast.success(`Uploaded ${fileName}`);
    } catch (err) {
      setUploadError(err.message || "Upload failed");
      toast.error("Geography upload failed");
    } finally {
      setUploading(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      // Delete from Storage
      await deleteFile(deleteTarget.storagePath);
      // Delete from Firestore geography index
      await deleteGeography(deleteTarget.id);
      // Refresh list
      await fetchGeographies();
      toast.success("Geography file deleted");
      setDeleteTarget(null);
    } catch (err) {
      setError(err.message || "Failed to delete");
      toast.error("Failed to delete geography file");
    } finally {
      setDeleting(false);
    }
  };

  // Group by state
  const groupedByState = geographies.reduce((acc, geo) => {
    if (!acc[geo.state]) {
      acc[geo.state] = { stateName: geo.stateName || geo.state, items: [] };
    }
    acc[geo.state].items.push(geo);
    return acc;
  }, {});

  return (
    <div>
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Geography Library
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Shared TopoJSON files used across client maps
          </p>
        </div>
        <button
          onClick={() => setShowUploadForm(!showUploadForm)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-secondary text-white text-sm font-medium rounded-lg hover:bg-secondary-dark transition shadow-sm self-start sm:self-auto"
        >
          <Upload size={18} />
          Upload TopoJSON
        </button>
      </div>

      {/* Upload form */}
      {showUploadForm && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">
            Upload New TopoJSON File
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            {/* State selector */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                State *
              </label>
              <select
                value={uploadState}
                onChange={(e) => {
                  setUploadState(e.target.value);
                  setUploadError(null);
                }}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary/30 focus:border-secondary"
              >
                <option value="">Select state...</option>
                {US_STATES.map((s) => (
                  <option key={s.abbr} value={s.abbr}>
                    {s.name} ({s.abbr})
                  </option>
                ))}
              </select>
            </div>

            {/* Geography type */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Geography Type *
              </label>
              <select
                value={uploadGeoType}
                onChange={(e) => {
                  setUploadGeoType(e.target.value);
                  setUploadError(null);
                }}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary/30 focus:border-secondary"
              >
                <option value="">Select type...</option>
                <option value="ZCTA">ZCTA (ZIP Codes)</option>
                <option value="BG">BG (Block Groups)</option>
              </select>
            </div>
          </div>

          {/* Region / custom name (optional) */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Region / Custom Name{" "}
              <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={uploadRegionLabel}
              onChange={(e) => {
                setUploadRegionLabel(e.target.value);
                setUploadError(null);
              }}
              placeholder="e.g. Los Angeles, Greater Hartford, NY Border"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary/30 focus:border-secondary"
            />
            <p className="text-xs text-gray-400 mt-1">
              Leave blank for full-state files. Use for partial-state or custom
              boundaries.
            </p>
            {uploadRegionSlug && (
              <p className="text-xs text-gray-400 mt-0.5">
                Slug:{" "}
                <span className="font-mono text-gray-500">
                  {uploadRegionSlug}
                </span>
              </p>
            )}
          </div>

          {/* Existing check */}
          {uploadState &&
            uploadGeoType &&
            geoExists(uploadState, uploadGeoType, uploadRegionSlug) && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700 flex items-center gap-2">
                <AlertCircle size={16} />
                {uploadState} {uploadGeoType}
                {uploadRegionSlug ? ` (${uploadRegionLabel})` : ""} already
                exists. Delete it first to re-upload.
              </div>
            )}

          {/* Storage path preview */}
          {uploadState &&
            uploadGeoType &&
            !geoExists(uploadState, uploadGeoType, uploadRegionSlug) && (
              <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-xs text-gray-500">
                  Will upload to:{" "}
                  <span className="font-mono text-gray-700">
                    shared/geojson/{uploadGeoType}/
                    {uploadRegionSlug
                      ? `${uploadState}_${uploadRegionSlug}.json`
                      : `${uploadState}.json`}
                  </span>
                </p>
              </div>
            )}

          {/* File uploader */}
          <FileUploader
            onUpload={handleUpload}
            accept=".json"
            acceptLabel="TopoJSON / GeoJSON files (.json)"
            maxSizeMB={100}
            disabled={
              !uploadState ||
              !uploadGeoType ||
              geoExists(uploadState, uploadGeoType, uploadRegionSlug)
            }
          />

          {/* Upload error */}
          {uploadError && (
            <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
              <AlertCircle size={14} />
              {uploadError}
            </p>
          )}

          {/* Cancel */}
          <div className="mt-4 flex justify-end">
            <button
              onClick={() => {
                setShowUploadForm(false);
                setUploadError(null);
                setUploadRegionLabel("");
              }}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
          <AlertCircle size={16} />
          {error}
          <button
            onClick={fetchGeographies}
            className="ml-auto underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <RefreshCw
            size={24}
            className="mx-auto text-gray-400 animate-spin mb-3"
          />
          <p className="text-sm text-gray-500">Loading geography index...</p>
        </div>
      )}

      {/* Empty state */}
      {!loading && geographies.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Globe size={40} className="mx-auto text-gray-300 mb-3" />
          <h3 className="text-lg font-semibold text-gray-700 mb-1">
            No geography files yet
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Upload TopoJSON files so client maps can reference them.
          </p>
          <button
            onClick={() => setShowUploadForm(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-secondary text-white text-sm font-medium rounded-lg hover:bg-secondary-dark transition"
          >
            <Upload size={16} />
            Upload First File
          </button>
        </div>
      )}

      {/* Geography list */}
      {!loading && geographies.length > 0 && (
        <div className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="text-2xl font-bold text-gray-900">
                {geographies.length}
              </div>
              <div className="text-xs text-gray-500">Total Files</div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="text-2xl font-bold text-gray-900">
                {Object.keys(groupedByState).length}
              </div>
              <div className="text-xs text-gray-500">States</div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="text-2xl font-bold text-gray-900">
                {geographies
                  .reduce((sum, g) => sum + (g.featureCount || 0), 0)
                  .toLocaleString()}
              </div>
              <div className="text-xs text-gray-500">Total Features</div>
            </div>
          </div>

          {/* Grouped by state */}
          {Object.entries(groupedByState)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([stateAbbr, { stateName, items }]) => (
              <div
                key={stateAbbr}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden"
              >
                <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <Map size={16} className="text-gray-500" />
                    <h3 className="text-sm font-semibold text-gray-800">
                      {stateName} ({stateAbbr})
                    </h3>
                    <span className="text-xs text-gray-400">
                      {items.length} {items.length === 1 ? "file" : "files"}
                    </span>
                  </div>
                </div>

                <div className="divide-y divide-gray-100">
                  {items
                    .sort((a, b) =>
                      a.geographyType.localeCompare(b.geographyType),
                    )
                    .map((geo) => {
                      const uploadDate = geo.uploadedAt?.toDate
                        ? geo.uploadedAt.toDate()
                        : geo.uploadedAt instanceof Date
                          ? geo.uploadedAt
                          : null;

                      return (
                        <div
                          key={geo.id}
                          className="px-5 py-3 flex items-center gap-4"
                        >
                          {/* Icon */}
                          <div className="p-2 bg-gray-100 rounded-lg">
                            <Layers size={16} className="text-gray-500" />
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-800">
                                {geo.geographyType === "ZCTA"
                                  ? "ZIP Codes (ZCTA)"
                                  : geo.geographyType === "BG"
                                    ? "Block Groups (BG)"
                                    : geo.geographyType}
                              </span>
                              {geo.regionLabel && (
                                <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium text-indigo-700 bg-indigo-50 rounded">
                                  {geo.regionLabel}
                                </span>
                              )}
                              {geo.featureCount && (
                                <span className="text-xs text-gray-400">
                                  {geo.featureCount.toLocaleString()} features
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-400 font-mono mt-0.5">
                              {geo.storagePath}
                            </p>
                            {uploadDate && (
                              <p className="text-xs text-gray-400 mt-0.5">
                                Uploaded{" "}
                                {uploadDate.toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })}
                              </p>
                            )}
                          </div>

                          {/* Actions */}
                          <button
                            onClick={() => setDeleteTarget(geo)}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                            title="Delete this geography file"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      );
                    })}
                </div>
              </div>
            ))}

          {/* Refresh button */}
          <div className="text-center">
            <button
              onClick={fetchGeographies}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 transition"
            >
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        loading={deleting}
        title={`Delete ${deleteTarget?.state} ${deleteTarget?.geographyType}${deleteTarget?.regionLabel ? ` (${deleteTarget.regionLabel})` : ""}?`}
        message={
          <>
            This will delete the TopoJSON file from Firebase Storage. Any client
            maps that reference {deleteTarget?.state}{" "}
            {deleteTarget?.geographyType}
            {deleteTarget?.regionLabel
              ? ` (${deleteTarget.regionLabel})`
              : ""}{" "}
            will lose their geographic boundaries until a new file is uploaded.
          </>
        }
        confirmLabel="Delete Geography"
        confirmStyle="danger"
      />
    </div>
  );
};

export default GeographyLibrary;
